import styles from './downloadTruckPreparationPDFModal.module.css'
import { useEffect, useState } from 'react'
import { fileHelpers, usePDFActions } from '../../../../features/files'
import { FileTile, FileType, Modal, ModalId } from '../../../../features/ui'
import { describeHelpers, useDataTypesHooks } from '../../../../features/data-types'
import { addressHelpers } from '../../../../features/address'
import { Roadmap, useDriverLoaders, useRoadmapHooks, useVehicleLoaders } from '../../../logistics'
import { operationHelpers, useOperationLoaders } from '../../../operations'
import { SaleOperation, useCustomerLoaders } from '../../../sales'
import { Product, useProductLoaders } from '../../../products'
import { useDeliveryHooks } from '../../../deliveries'
import { useBranchLoaders } from '../../../manager'
import { TruckPreparationPDF } from '../truck-preparation-pdf/truckPreparationPDF'
import { TruckPreparationPDFData } from '../truck-preparation-pdf/types'

type Props = {
    roadmap?: Roadmap
}

export const DownloadTruckPreparationPDFModal = (props: Props) => {
    const { roadmap } = props

    const [data, setData] = useState<TruckPreparationPDFData>()

    const { truckPreparationFileName } = fileHelpers
    const { describeCode } = describeHelpers
    const { describeCode: describeDeliveryCode } = operationHelpers
    const { describeAddress } = addressHelpers
    const { formatDate } = useDataTypesHooks()
    const { describeQuantity } = useDeliveryHooks()
    const { getRouteData } = useRoadmapHooks()

    const operationLoaders = useOperationLoaders()
    const productLoaders = useProductLoaders()
    const customerLoaders = useCustomerLoaders()
    const branchLoaders = useBranchLoaders()
    const vehicleLoaders = useVehicleLoaders()
    const driverLoaders = useDriverLoaders()

    const { generatePDF, printPDF, isPrinting } = usePDFActions()

    const pdfId = 'truck-preparation-pdf'
    const pdfName = roadmap && data ? truckPreparationFileName(roadmap.code) : undefined

    const describeProduct = (product: Product) => `${product.name}${product.supplierCode ? ` (${product.supplierCode})` : ''}`

    const loadData = async (currentRoadmap: Roadmap): Promise<TruckPreparationPDFData> => {
        const { shipments, shipmentAddresses } = await getRouteData(currentRoadmap)
        const operations = await operationLoaders().loadDeliveriesOperations(shipments) as SaleOperation[]
        const findOperation = (id: string) => operations.find(operation => operation.id === id)!

        const productIds = Array.from(new Set(operations.flatMap(operation => operation.items.map(item => item.productId))))
        const customerIds = Array.from(new Set(operations.map(operation => operation.customerId)))
        const [products, customers, vehicle, driver] = await Promise.all([
            productLoaders().loadProducts(productIds),
            customerLoaders().loadCustomers(customerIds),
            vehicleLoaders().loadVehicle(currentRoadmap.vehicleId),
            driverLoaders().loadDriver(currentRoadmap.driverId)
        ])
        const findProduct = (id: string) => products.find(product => product.id === id)!

        // Shipment items with their product, leaving out the ones that aren't delivered in this shipment
        const shipmentItems = (shipment: typeof shipments[number], operation: SaleOperation) =>
            shipment.items
                .filter(shipmentItem => shipmentItem.quantity > 0)
                .map(shipmentItem => {
                    const item = operation.items.find(item => item.id === shipmentItem.itemId)!
                    return { product: findProduct(item.productId), quantity: shipmentItem.quantity }
                })

        // The last shipment to deliver is loaded first
        const loadShipments = [...shipments].reverse().map((shipment, index) => {
            const address = shipmentAddresses.find(address => address.id === shipment.addressId)
            const operation = findOperation(shipment.operationId)
            const customer = customers.find(customer => customer.id === operation.customerId)
            return {
                title: `${index + 1}) Envío ${describeDeliveryCode(shipment)}${address ? `: ${describeAddress(address)}` : ''}`,
                customer: customer ? `Cliente: ${customer.name}` : undefined,
                rows: shipmentItems(shipment, operation).map(({ product, quantity }) => ({
                    product: describeProduct(product),
                    quantity: describeQuantity(quantity, product)
                }))
            }
        })

        const branch = branchLoaders().findBranch(currentRoadmap.branchId)

        return {
            roadmapCode: describeCode(currentRoadmap),
            date: formatDate(new Date(currentRoadmap.date)),
            branch: branch?.name || '-',
            vehicle: vehicle ? `${vehicle.name} (${vehicle.plate})` : '-',
            driver: driver?.name || '-',
            shipments: loadShipments
        }
    }

    useEffect(() => {
        setData(undefined)
        roadmap && loadData(roadmap).then(setData)
    }, [roadmap])

    const onSubmit = () => {
        pdfName && generatePDF(pdfId, pdfName)
    }

    const renderHiddenPDF = () => roadmap && data ? (
        <div className={styles.hidden}>
            <TruckPreparationPDF
                id={pdfId}
                roadmap={roadmap}
                data={data}
            />
        </div>
    ) : <></>

    return (
        <Modal
            modalId={ModalId.DOWNLOAD_TRUCK_PREPARATION_PDF}
            title="Descargar Preparación de Camión"
            submitLabel="Descargar"
            submitIcon="download"
            disableSubmit={!pdfName}
            onSubmit={onSubmit}
            secondaryLabel="Imprimir"
            secondaryIcon="printer"
            disableSecondary={isPrinting || !pdfName}
            onSecondarySubmit={() => printPDF(pdfId)}
        >
            <FileTile fileType={FileType.PDF} fileName={pdfName} />
            {renderHiddenPDF()}
        </Modal>
    )
}
